import React, { useState, useContext, useEffect } from 'react';
import { View, Text, TextInput, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, KeyboardAvoidingView, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { showAlert } from '../services/confirm';
import storage from '../services/storage';
import Colors from '../constants/Colors';
import * as Haptics from 'expo-haptics';
import { Ionicons } from '@expo/vector-icons';

const theme = Colors.light;
const { spacing, radius } = Colors;

export default function AddScoreScreen({ navigation }) {
    const { user, refreshPartner } = useContext(AuthContext);
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [recentReasons, setRecentReasons] = useState([]);
    const [historyTags, setHistoryTags] = useState([]);
    const [loadingTags, setLoadingTags] = useState(true);
    const [activeTags, setActiveTags] = useState([]); // Array of tags for multi-select logic if desired, or single tag quick select

    const [category, setCategory] = useState('other');

    const categories = [
        { id: 'housework', label: '家务', icon: 'home', color: theme.green },
        { id: 'romance', label: '浪漫', icon: 'heart', color: theme.systemPink },
        { id: 'emotion', label: '情绪', icon: 'happy', color: theme.secondary },
        { id: 'growth', label: '成长', icon: 'trending-up', color: theme.blue },
        { id: 'other', label: '其他', icon: 'apps', color: theme.systemGray },
    ];

    useEffect(() => {
        loadRecentReasons();
        loadHistoryTags();
    }, []);

    const loadRecentReasons = async () => {
        const saved = await storage.getItem('recent_reasons');
        if (saved) setRecentReasons(JSON.parse(saved));
    };

    const loadHistoryTags = async () => {
        try {
            const res = await api.get('/score/history');
            const scores = res.data;
            const tagMap = new Map();
            const tagCount = {};
            scores.forEach(s => {
                const key = `${s.reason}|${s.amount}`;
                if (!tagMap.has(key)) tagMap.set(key, { text: s.reason, score: s.amount, category: s.category || 'other' });
                tagCount[key] = (tagCount[key] || 0) + 1;
            });
            const uniqueTags = Array.from(tagMap.values());
            uniqueTags.sort((a, b) => (tagCount[`${b.text}|${b.score}`] || 0) - (tagCount[`${a.text}|${a.score}`] || 0));
            setHistoryTags(uniqueTags.slice(0, 15)); // Limit to top 15
        } catch (error) {
            console.log('Load history tags error', error);
        } finally {
            setLoadingTags(false);
        }
    };

    const quickReason = (text, score, cat = 'other') => {
        Haptics.selectionAsync();
        setReason(text);
        setAmount(score.toString());
        setCategory(cat);
    };

    const saveReason = async (text) => {
        const updated = [text, ...recentReasons.filter(r => r !== text)].slice(0, 10);
        setRecentReasons(updated);
        await storage.setItem('recent_reasons', JSON.stringify(updated));
    };

    const handleSubmit = async () => {
        if (!amount || !reason) {
            Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning);
            showAlert('提示', '请输入分值和理由');
            return;
        }

        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
        try {
            await api.post('/score', { amount: parseInt(amount), reason, category });
            await saveReason(reason);
            refreshPartner();
            navigation.goBack();
        } catch (error) {
            Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
            showAlert('失败', error.response?.data?.message || '添加失败');
        }
    };

    const previewScore = parseInt(amount) || 0;
    const partnerDisplayName = user?.partnerNickname || user?.partnerUsername || '伴侣';

    return (
        <SafeAreaView style={styles.container} edges={['top']}>
            {/* Custom Modal Header */}
            <View style={styles.header}>
                <TouchableOpacity onPress={() => navigation.goBack()} style={styles.headerBtn}>
                    <Text style={styles.cancelText}>取消</Text>
                </TouchableOpacity>
                <Text style={styles.headerTitle}>记一笔</Text>
                <TouchableOpacity
                    onPress={handleSubmit}
                    style={styles.headerBtn}
                    disabled={!amount || !reason}
                >
                    <Text style={[styles.doneText, (!amount || !reason) && styles.disabledText]}>添加</Text>
                </TouchableOpacity>
            </View>

            <KeyboardAvoidingView
                behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                style={{ flex: 1 }}
            >
                <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>

                    {/* Score Preview */}
                    <View style={styles.previewContainer}>
                        <View style={styles.previewCard}>
                            <Text style={styles.previewTitle} numberOfLines={1}>{partnerDisplayName}</Text>
                            <Text style={styles.previewSubtitle}>本次预览</Text>
                            <View style={[
                                styles.previewBadge,
                                previewScore >= 0 ? styles.previewBadgeGain : styles.previewBadgeLoss,
                            ]}>
                                <Text style={[
                                    styles.previewBadgeText,
                                    previewScore >= 0 ? styles.previewBadgeTextGain : styles.previewBadgeTextLoss,
                                ]}>
                                    {previewScore > 0 ? '+' : ''}{previewScore}
                                </Text>
                            </View>
                        </View>
                    </View>

                    {/* Category Selector */}
                    <View style={styles.section}>
                        <Text style={styles.groupLabel}>分类</Text>
                        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.categoryRow}>
                            {categories.map((cat) => (
                                <TouchableOpacity
                                    key={cat.id}
                                    style={[
                                        styles.categoryChip,
                                        category === cat.id && styles.categoryChipActive
                                    ]}
                                    onPress={() => {
                                        Haptics.selectionAsync();
                                        setCategory(cat.id);
                                    }}
                                >
                                    <Ionicons
                                        name={cat.icon}
                                        size={16}
                                        color={category === cat.id ? '#fff' : theme.textSecondary}
                                    />
                                    <Text style={[
                                        styles.categoryText,
                                        category === cat.id && styles.categoryTextActive
                                    ]}>{cat.label}</Text>
                                </TouchableOpacity>
                            ))}
                        </ScrollView>
                    </View>

                    {/* Common Tags */}
                    {loadingTags ? (
                        <View style={styles.loadingBox}>
                            <ActivityIndicator size="small" color={theme.muted} />
                        </View>
                    ) : historyTags.length > 0 ? (
                        <View style={styles.section}>
                            <Text style={styles.groupLabel}>常用</Text>
                            <View style={styles.tagGrid}>
                                {historyTags.map((tag, index) => {
                                    const isPositive = tag.score > 0;
                                    const isSelected = reason === tag.text && amount === tag.score.toString();
                                    return (
                                        <TouchableOpacity
                                            key={index}
                                            style={[styles.tag, isSelected && styles.tagSelected]}
                                            onPress={() => quickReason(tag.text, tag.score, tag.category)}
                                            activeOpacity={0.6}
                                        >
                                            <Text style={[styles.tagText, isSelected && styles.tagTextSelected]}>
                                                {tag.text}
                                            </Text>
                                            <Text style={[
                                                styles.tagScore,
                                                { color: isPositive ? theme.scoreGain : theme.scoreLoss },
                                                isSelected && { color: '#fff' }
                                            ]}>
                                                {isPositive ? '+' : ''}{tag.score}
                                            </Text>
                                        </TouchableOpacity>
                                    );
                                })}
                            </View>
                        </View>
                    ) : null}

                    {/* Custom Input */}
                    <View style={styles.section}>
                        <Text style={styles.groupLabel}>自定义</Text>
                        <View style={styles.formGroup}>
                            <View style={styles.inputRow}>
                                <Text style={styles.inputLabel}>分值</Text>
                                <TextInput
                                    style={styles.input}
                                    placeholder="0"
                                    placeholderTextColor={theme.textTertiary}
                                    value={amount}
                                    onChangeText={setAmount}
                                    keyboardType="numeric"
                                />
                            </View>
                            <View style={styles.divider} />
                            <View style={styles.inputRow}>
                                <Text style={styles.inputLabel}>原因</Text>
                                <TextInput
                                    style={styles.input}
                                    placeholder="输入原因..."
                                    placeholderTextColor={theme.textTertiary}
                                    value={reason}
                                    onChangeText={setReason}
                                />
                            </View>
                        </View>
                    </View>

                    <View style={{ height: spacing.xxxl }} />
                </ScrollView>
            </KeyboardAvoidingView>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.backgroundGrouped,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: spacing.lg,
        paddingVertical: spacing.md,
        backgroundColor: theme.backgroundSecondary,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: theme.border,
    },
    headerBtn: {
        padding: spacing.xs,
    },
    cancelText: {
        fontSize: 17,
        color: theme.blue,
    },
    doneText: {
        fontSize: 17,
        fontWeight: '600',
        color: theme.blue,
    },
    disabledText: {
        color: theme.textTertiary,
    },
    headerTitle: {
        fontSize: 17,
        fontWeight: '600',
        color: theme.text,
    },
    content: {
        padding: spacing.lg,
    },
    previewContainer: {
        alignItems: 'center',
        marginBottom: spacing.xl,
        marginTop: spacing.sm,
    },
    previewCard: {
        width: 172,
        height: 102,
        borderRadius: 20,
        backgroundColor: theme.backgroundSecondary,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: 'rgba(0,0,0,0.06)',
        ...theme.shadowCard,
    },
    previewTitle: {
        fontSize: 15,
        fontWeight: '600',
        color: theme.text,
        maxWidth: 130,
    },
    previewSubtitle: {
        marginTop: 4,
        fontSize: 12,
        color: theme.textSecondary,
    },
    previewBadge: {
        marginTop: 12,
        minWidth: 70,
        height: 36,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 10,
    },
    previewBadgeGain: {
        backgroundColor: theme.scoreGainBg,
    },
    previewBadgeLoss: {
        backgroundColor: theme.scoreLossBg,
    },
    previewBadgeText: {
        fontSize: 31,
        fontWeight: '700',
        letterSpacing: -0.4,
    },
    previewBadgeTextGain: {
        color: theme.scoreGain,
    },
    previewBadgeTextLoss: {
        color: theme.scoreLoss,
    },
    section: {
        marginBottom: spacing.xl,
    },
    groupLabel: {
        fontSize: 13,
        fontWeight: '400',
        color: theme.textSecondary,
        marginLeft: spacing.xs,
        marginBottom: spacing.sm,
        textTransform: 'uppercase',
    },

    // Chips
    categoryRow: {
        paddingHorizontal: spacing.xs,
        paddingBottom: spacing.xs,
        gap: spacing.sm,
    },
    categoryChip: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 8,
        paddingHorizontal: 12,
        backgroundColor: theme.backgroundSecondary,
        borderRadius: radius.pill,
        borderWidth: 1,
        borderColor: theme.border,
        gap: 6,
    },
    categoryChipActive: {
        backgroundColor: theme.blue,
        borderColor: theme.blue,
    },
    categoryText: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.textSecondary,
    },
    categoryTextActive: {
        color: '#fff',
        fontWeight: '600',
    },

    // Tags
    tagGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: spacing.sm,
    },
    tag: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 8,
        paddingHorizontal: 12,
        backgroundColor: theme.card,
        borderRadius: radius.md,
        gap: spacing.sm,
        borderWidth: StyleSheet.hairlineWidth,
        borderColor: theme.border,
        ...theme.shadowCard,
    },
    tagSelected: {
        backgroundColor: theme.blue,
        borderColor: theme.blue,
    },
    tagText: {
        fontSize: 14,
        color: theme.text,
    },
    tagTextSelected: {
        color: '#fff',
        fontWeight: '600',
    },
    tagScore: {
        fontSize: 13,
        fontWeight: '600',
    },

    // Form
    formGroup: {
        backgroundColor: theme.backgroundSecondary,
        borderRadius: radius.card,
        paddingLeft: spacing.lg,
    },
    inputRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: spacing.md,
        paddingRight: spacing.lg,
        minHeight: 56,
    },
    inputLabel: {
        fontSize: 17,
        color: theme.text,
        width: 60,
    },
    input: {
        flex: 1,
        fontSize: 17,
        color: theme.text,
        textAlign: 'right',
    },
    divider: {
        height: StyleSheet.hairlineWidth,
        backgroundColor: theme.border,
    },

    loadingBox: {
        padding: spacing.xl,
        alignItems: 'center',
    },
});
